import { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { Card, CardBody, CardHeader, Button } from "reactstrap";
import axios from "axios";
import requests from "../../utils/Requests";
import encryptStorage from "../../utils/Encryptstorage";

const RowItem = ({ label, value }) => (
  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
    <strong>{label}</strong>
    <span>{value}</span>
  </div>
);

const BurjSaveConfirmation = () => {
  const history = useHistory();
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const data = encryptStorage.getItem("burjSavePreview");
    if (data) {
      try {
        setPreview(JSON.parse(data));
      } catch (e) {
        setPreview(null);
      }
    }
  }, []);

  if (!preview) {
    return (
      <div className="container">
        <Card className="mt-4">
          <CardHeader>Confirmation</CardHeader>
          <CardBody>Nothing to preview.</CardBody>
        </Card>
      </div>
    );
  }

  const { payload, response } = preview;
  const bookingId = response?.getSaveTicketDto?.ResponseData?.BookingId;

  return (
    <div className="container">
      <Card className="mt-4">
        <CardHeader>Review Your Details</CardHeader>
        <CardBody>
          <RowItem label="Guest Name" value={payload.GuestName} />
          <RowItem label="Guest Email" value={payload.GuestEmail} />
          <RowItem label="Guest Mobile" value={payload.GuestMobile} />
          <hr />
          <RowItem label="Product Id" value={payload.TimeSlot?.ProductId} />
          <RowItem label="Event ID" value={payload.TimeSlot?.EventID} />
          <RowItem label="Start" value={payload.TimeSlot?.StartDateTime} />
          <RowItem label="End" value={payload.TimeSlot?.EndDateTime} />
          <hr />
          {Array.isArray(payload.Tickets) && (
            <div>
              <strong>Tickets</strong>
              {payload.Tickets.filter(t => (t.Quantity || 0) > 0).map((t, idx) => (
                <div key={idx} style={{ paddingLeft: 8, marginTop: 6 }}>
                  <div>{t.TicketGroup} - {t.TicketDescription}</div>
                  <small>Code: {t.TicketCode} | Id: {t.TicketId} | Price: AED {t.TicketPrice} | Qty: {t.Quantity}</small>
                </div>
              ))}
            </div>
          )}
          <div className="d-flex justify-content-end" style={{ gap: 12, marginTop: 16 }}>
            <Button color="secondary" onClick={() => history.goBack()}>Cancel</Button>
            <Button
              color="primary"
              onClick={async () => {
                if (!bookingId) {
                  alert("Missing BookingId from Save Ticket response");
                  return;
                }
                try {
                  // derive supporting objects
                  // prefer login context stored as 'enl' in other booking flows
                  const enlRaw = encryptStorage.getItem("enl");
                  let loginContext = null;
                  try { loginContext = enlRaw ? JSON.parse(enlRaw) : null; } catch (e) { loginContext = null; }

                  // derive counts from selected tickets
                  const selectedTickets = Array.isArray(payload?.Tickets)
                    ? payload.Tickets.filter(t => (t.Quantity || 0) > 0)
                    : [];
                  const adult = selectedTickets.find(t => (t.TicketGroup || '').toLowerCase() === 'adult');
                  const child = selectedTickets.find(t => (t.TicketGroup || '').toLowerCase() === 'child');
                  const bookNofAdult = adult?.Quantity || 0;
                  const bookNofChild = child?.Quantity || 0;

                  // choose a ticket type id from selection (fallback to first selected ticket)
                  const primaryTicket = adult || selectedTickets[0] || {};
                  const ticketTypeId = String(primaryTicket.TicketId || primaryTicket.TicketID || primaryTicket.TicketCode || "");

                  // compute total
                  const bookTotal = selectedTickets.reduce((sum, t) => sum + (Number(t.TicketPrice || 0) * Number(t.Quantity || 0)), 0);

                  // travel date and time slot
                  const startDt = payload?.TimeSlot?.StartDateTime || "";
                  const endDt = payload?.TimeSlot?.EndDateTime || "";
                  const bookTravellDate = startDt ? String(startDt).slice(0, 10) : ""; // YYYY-MM-DD
                  const toHm = (dt) => {
                    if (!dt || typeof dt !== 'string') return '';
                    // expected format: YYYY-MM-DD HH:mm:ss
                    const parts = dt.split(' ');
                    if (parts.length < 2) return '';
                    const time = parts[1];
                    const hm = time.slice(0, 5);
                    return hm;
                  };
                  const startHm = toHm(startDt);
                  const endHm = toHm(endDt);
                  const bookTravelTimeSlot = startHm && endHm && bookTravellDate
                    ? `${bookTravellDate} ${startHm} - ${endHm}`
                    : (startDt || "");

                  // agency context
                  const isB2b = loginContext?.b2b === true;
                  const isB2bUser = loginContext?.b2bUser === true;
                  const bookB2bId = isB2b ? (loginContext?.agency?.agencyId || 0) : 0;
                  const bookB2bUserId = isB2bUser ? (loginContext?.agencyUser?.agencyUserId || 0) : 0;

                  // agent ref if available anywhere
                  let agentRefNumber = payload?.agentRefNumber || "";
                  if (!agentRefNumber) {
                    // fallback to previous booking object if exists
                    const dObjRaw = encryptStorage.getItem("DFuck");
                    try {
                      const dObj = dObjRaw ? JSON.parse(dObjRaw) : null;
                      agentRefNumber = dObj?.agentRefNumber || "";
                    } catch (e) { /* ignore */ }
                  }

                  const bookingDto = {
                    platformId: 1,
                    bookedByBackOffice: false,
                    bookedCreditLimit: 1,
                    bookB2bId: bookB2bId,
                    bookB2bUserId: bookB2bUserId,
                    attractionsId: "45",
                    bookCustomerName: payload?.GuestName || "",
                    bookCustomerEmail: payload?.GuestEmail || "",
                    bookMobileNumber: payload?.GuestMobile || "",
                    ticketTypeId: ticketTypeId,
                    bookNofAdult: String(bookNofAdult),
                    bookNofChild: bookNofChild,
                    bookTravellDate: bookTravellDate,
                    bookTravelTimeSlot: bookTravelTimeSlot,
                    bookPaymentMode: "2",
                    bookTotal: bookTotal,
                    secretKey: requests.apiKey,
                    agentRefNumber: agentRefNumber,
                  };

                  const res = await axios.post(requests.confirmBurjKhalifaTicket, {
                    BookingId: bookingId,
                    IsEticketRequired: true,
                    bookingDto: bookingDto,
                  });
                  const ok = res?.data?.getConfirmTicketDto?.ResultStatus === true;
                  if (ok) {
                    encryptStorage.setItem("burjConfirmResult", JSON.stringify(res.data));
                    history.push("/burj-ticket-success");
                  } else {
                    alert("Failed to confirm ticket. Please try again.");
                  }
                } catch (e) {
                  alert("Error calling confirm API");
                }
              }}
            >
              Confirm
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default BurjSaveConfirmation;


